/**
 * The figures both Statistics pages print around the findings: the period's
 * range and what it is compared with, the four key figures with their trend,
 * and the three tables underneath — by model · effort, by kind of step, by day.
 *
 * Values, not strings, wherever a page sorts by them; the words where both
 * pages say the same sentence. Pure — the desktop app and the account area call
 * the same functions on the same report, so a figure cannot read one way on the
 * machine and another in the organization's page.
 */
import {
  cacheHitRatio,
  costPerLanded,
  formatPercent,
  formatShortDay,
  formatUsd,
  kindLabel,
  pairLabel,
  successRate,
  tokensOf,
  type DayPoint,
  type Totals
} from './analysis';
import { STEP_KINDS, type StepKind } from './day';
import { MIN_SAMPLE, change, median } from './findings';
import type { UsageReport } from './report';

/* ── the range ────────────────────────────────────────────────────────────── */

/**
 * `7 Sep – 6 Oct 2026` against `8 Aug – 6 Sep`. With nothing kept before today
 * (`fresh`), the period is one day and there is nothing to compare it with.
 */
export function rangeWords(
  report: Pick<UsageReport, 'from' | 'to' | 'prevFrom' | 'prevTo' | 'previous'>,
  fresh = false
): { range: string; against: string } {
  if (fresh) return { range: `Today, ${formatShortDay(report.to)}`, against: 'nothing before it to compare with' };
  const prev = { from: report.prevFrom, to: report.prevTo };
  const earlier = report.previous.totals.turns > 0 || report.previous.totals.started > 0;
  const span = (from: string, to: string, year: boolean): string =>
    `${formatShortDay(from, year && from.slice(0, 4) !== to.slice(0, 4))} – ${formatShortDay(to, year)}`;
  return {
    range: span(report.from, report.to, true),
    against: earlier ? `against ${span(prev.from, prev.to, false)}` : 'nothing before it to compare with'
  };
}

/* ── the key figures ─────────────────────────────────────────────────────── */

export interface KeyFigure {
  label: string;
  value: string;
  /** The trend against the period before, or why there is none. */
  line: string;
  /** What the value is made of. */
  sub: string;
  /** A line beside the value, one point per day — null where a day has nothing to divide by. */
  spark: (number | null)[];
}

/** Over the last seven days of each day, null where those seven hold nothing to divide by. */
function rolling(daily: DayPoint[], of: (sum: { cost: number; landed: number; started: number }) => number | null): (number | null)[] {
  return daily.map((_, i) => {
    const week = daily.slice(Math.max(0, i - 6), i + 1);
    return of(
      week.reduce(
        (acc, d) => ({ cost: acc.cost + d.cost, landed: acc.landed + d.landed, started: acc.started + d.started }),
        { cost: 0, landed: 0, started: 0 }
      )
    );
  });
}

const NO_EARLIER = 'No earlier period to compare with yet.';

export function keyFigures(report: UsageReport, previousLabel: string): KeyFigure[] {
  const t = report.current.totals;
  const p = report.previous.totals;
  const trend = (now: number | null, before: number | null): string | null => change(now, before, previousLabel)?.words ?? null;
  const cpl = costPerLanded(t);
  const rate = successRate(t);
  const before = successRate(p);
  const points = rate !== null && before !== null ? Math.round((rate - before) * 100) : null;
  const daily = report.current.daily;
  return [
    {
      label: 'SPENT',
      value: formatUsd(t.cost),
      line: trend(t.cost, p.cost) ?? NO_EARLIER,
      sub: 'at API list prices',
      spark: daily.map((d) => d.cost)
    },
    {
      label: 'LANDED',
      value: String(t.landed),
      line: trend(t.landed, p.landed) ?? NO_EARLIER,
      sub: `${t.started} started`,
      spark: daily.map((d) => d.landed)
    },
    {
      label: 'PER LANDED TASK',
      value: cpl === null ? '—' : formatUsd(cpl),
      line: trend(cpl, costPerLanded(p)) ?? (cpl === null ? 'Nothing landed yet.' : NO_EARLIER),
      sub: `${formatUsd(t.cost)} ÷ ${t.landed}`,
      spark: rolling(daily, (s) => (s.landed ? s.cost / s.landed : null))
    },
    {
      label: 'LANDED OF STARTED',
      value: rate === null ? '—' : formatPercent(rate),
      line:
        points === null
          ? NO_EARLIER
          : Math.abs(points) < 1
            ? `about the same as ${previousLabel}`
            : `${points < 0 ? 'down' : 'up'} ${Math.abs(points)} points from ${previousLabel}`,
      sub: `${t.landed} of ${t.started} · ${t.failed} failed, ${t.stopped} stopped`,
      spark: rolling(daily, (s) => (s.started ? s.landed / s.started : null))
    }
  ];
}

/**
 * A series as the points of a 160 × 36 line, with its dashed level and its
 * marked day — or null with fewer than two values to join. Both pages draw it
 * at that size, or stretch it (`preserveAspectRatio="none"`).
 */
export function sparkGeometry(
  values: readonly (number | null)[],
  opt: { ref?: number; mark?: number; zero?: boolean } = {}
): { points: string; refY?: number; mx?: number; my?: number } | null {
  const W = 160;
  const H = 36;
  const known = values.flatMap((v, i) => (v === null ? [] : [[i, v] as const]));
  if (known.length < 2) return null;
  const all = [...known.map(([, v]) => v), ...(opt.ref !== undefined ? [opt.ref] : [])];
  const lo = Math.min(...all, opt.zero ? 0 : Infinity);
  const hi = Math.max(...all);
  const n = values.length - 1 || 1;
  const x = (i: number): number => Math.round(((i / n) * (W - 4) + 2) * 10) / 10;
  const y = (v: number): number => Math.round((H - 3 - ((v - lo) / (hi - lo || 1)) * (H - 6)) * 10) / 10;
  const markValue = opt.mark !== undefined ? values[opt.mark] : null;
  return {
    points: known.map(([i, v]) => `${x(i)},${y(v)}`).join(' '),
    ...(opt.ref !== undefined ? { refY: y(opt.ref) } : {}),
    ...(opt.mark !== undefined && markValue !== null && markValue !== undefined ? { mx: x(opt.mark), my: y(markValue) } : {})
  };
}

/* ── the tables ──────────────────────────────────────────────────────────── */

export interface PairFigure {
  key: string;
  label: string;
  turns: number;
  tokens: number;
  cache: number | null;
  cost: number;
  started: number;
  landed: number;
  /** Null under three tasks started — `few` when there were one or two, and a rate says nothing. */
  rate: number | null;
  few: boolean;
  cpl: number | null;
  reruns: number;
  /** `4.0× median` — a landing far above the others'. */
  flag: string | null;
}

export interface KindFigure {
  key: StepKind;
  label: string;
  sub: string | null;
  turns: number;
  cost: number;
  share: number;
  /** `12 of 15` — a step kind's verdicts; null outside a workflow step. */
  steps: string | null;
  stepsPassed: number | null;
  again: number | null;
  /** The model · effort with the most turns of that kind. */
  mostly: string;
}

export interface DayFigure {
  key: string;
  label: string;
  today: boolean;
  cost: number;
  turns: number;
  started: number;
  landed: number;
  cpl: number | null;
  reruns: number;
  conflicts: number;
  flag: string | null;
}

export interface FigureTables {
  pairs: PairFigure[];
  kinds: KindFigure[];
  /** Newest first. */
  days: DayFigure[];
  totals: Totals & { tokens: number; cache: number | null; rate: number | null; cpl: number | null };
}

/** `2.4× median` beside every value over twice the median of its column — three values first. */
function flagger(values: (number | null)[]): (value: number | null) => string | null {
  const known = values.filter((v): v is number => v !== null && v > 0);
  const mid = known.length >= MIN_SAMPLE ? median(known) : 0;
  return (value) => (mid > 0 && value !== null && value > 2 * mid ? `${(value / mid).toFixed(1)}× median` : null);
}

export function figureTables(report: UsageReport, today: string): FigureTables {
  const { current } = report;
  const t = current.totals;
  const pairCpl = current.pairs.map((row) => costPerLanded(row));
  const pairFlag = flagger(pairCpl);
  const pairs = current.pairs.map((row, i): PairFigure => {
    const label = pairLabel(row.model, row.effort);
    return {
      key: label,
      label,
      turns: row.turns,
      tokens: tokensOf(row),
      cache: cacheHitRatio(row),
      cost: row.cost,
      started: row.started,
      landed: row.landed,
      rate: row.started >= MIN_SAMPLE ? successRate(row) : null,
      few: row.started > 0 && row.started < MIN_SAMPLE,
      cpl: pairCpl[i],
      reruns: row.reruns,
      flag: pairFlag(pairCpl[i])
    };
  });

  const mostly = new Map<StepKind, { label: string; turns: number }>();
  for (const b of current.buckets) {
    const turns = b.bucket.t ?? 0;
    const best = mostly.get(b.kind);
    if (!best || turns > best.turns) mostly.set(b.kind, { label: pairLabel(b.model, b.effort), turns });
  }
  const kinds = STEP_KINDS.flatMap((kind): KindFigure[] => {
    const row = current.kinds.find((one) => one.kind === kind);
    if (!row) return [];
    const verdicts = row.stepsPassed + row.stepsFailed;
    return [
      {
        key: kind,
        ...kindLabel(kind),
        turns: row.turns,
        cost: row.cost,
        share: t.cost > 0 ? row.cost / t.cost : 0,
        steps: kind === 'task' ? null : `${row.stepsPassed} of ${verdicts}`,
        stepsPassed: kind === 'task' ? null : row.stepsPassed,
        again: kind === 'task' ? null : row.stepReruns,
        mostly: mostly.get(kind)?.label ?? '—'
      }
    ];
  });

  const dayCpl = current.daily.map((d) => (d.landed ? d.cost / d.landed : null));
  const dayFlag = flagger(dayCpl);
  const days = current.daily
    .map((d, i): DayFigure => {
      const isToday = d.day === today;
      const date = new Date(`${d.day}T12:00:00Z`);
      const weekday = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getUTCDay()];
      return {
        key: d.day,
        label: `${weekday} ${formatShortDay(d.day)}${isToday ? ' · so far' : ''}`,
        today: isToday,
        cost: d.cost,
        turns: d.turns,
        started: d.started,
        landed: d.landed,
        cpl: dayCpl[i],
        reruns: d.reruns,
        conflicts: d.conflicts,
        flag: dayFlag(dayCpl[i])
      };
    })
    .reverse();

  return {
    pairs,
    kinds,
    days,
    totals: { ...t, tokens: tokensOf(t), cache: cacheHitRatio(t), rate: successRate(t), cpl: costPerLanded(t) }
  };
}
